import { Routes, Route } from 'react-router-dom'
import { Component, type ReactNode, type ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'
import { useAuth } from '@/hooks/useAuth'
import { CarLoadingScreen } from '@/components/shared/CarLoadingScreen'
import { OfflineBanner } from '@/components/shared/OfflineBanner'
import { PwaInstallBanner } from '@/components/shared/PwaInstallBanner'
import { CookieConsent } from '@/components/shared/CookieConsent'

// ─── Error Boundary ───────────────────────────────────────────────────────────

interface EBState { error: Error | null }

class AppErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { error: null }

  static getDerivedStateFromError(error: Error): EBState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
    if (import.meta.env.DEV) {
      console.error('[AppErrorBoundary]', error, info.componentStack)
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
          <h1 className="text-2xl font-bold text-destructive">Algo deu errado</h1>
          <p className="text-sm text-muted-foreground max-w-sm">
            {this.state.error.message}
          </p>
          <button
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
          >
            Voltar para o início
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// Public pages
import LandingPage from '@/pages/public/LandingPage'
import InstructorsPage from '@/pages/public/InstructorsPage'
import InstructorProfilePage from '@/pages/public/InstructorProfilePage'
import LoginPage from '@/pages/public/LoginPage'
import RegisterPage from '@/pages/public/RegisterPage'
import HowItWorksPage from '@/pages/public/HowItWorksPage'
import BeInstructorPage from '@/pages/public/BeInstructorPage'
import ResetPasswordPage from '@/pages/public/ResetPasswordPage'
import AuthCallbackPage from '@/pages/public/AuthCallbackPage'
import NotFoundPage from '@/pages/public/NotFoundPage'

// Student pages
import StudentDashboard from '@/pages/student/StudentDashboard'
import BookingFlow from '@/pages/student/BookingFlow'
import StudentBookingDetail from '@/pages/student/StudentBookingDetail'
import StudentProfile from '@/pages/student/StudentProfile'
import StudentReviews from '@/pages/student/StudentReviews'
import StudentOnboarding from '@/pages/student/StudentOnboarding'

// Instructor pages
import InstructorDashboard from '@/pages/instructor/InstructorDashboard'
import InstructorAgenda from '@/pages/instructor/InstructorAgenda'
import InstructorLessons from '@/pages/instructor/InstructorLessons'
import InstructorFinancial from '@/pages/instructor/InstructorFinancial'
import InstructorEditProfile from '@/pages/instructor/InstructorEditProfile'
import InstructorOnboarding from '@/pages/instructor/InstructorOnboarding'
import InstructorStudents from '@/pages/instructor/InstructorStudents'

// Public extra pages
import HelpPage from '@/pages/public/HelpPage'
import CancellationPolicyPage from '@/pages/public/CancellationPolicyPage'
import SecurityPage from '@/pages/public/SecurityPage'
import TermsPage from '@/pages/public/TermsPage'
import PrivacyPage from '@/pages/public/PrivacyPage'
import GiftCardPage from '@/pages/public/GiftCardPage'

// Shared pages
import NotificationsPage from '@/pages/shared/NotificationsPage'
import MessagesPage from '@/pages/shared/MessagesPage'

// Student extra pages
import StudentPayments from '@/pages/student/StudentPayments'
import StudentPremium from '@/pages/student/StudentPremium'
import StudentQuiz from '@/pages/student/StudentQuiz'
import StudentAchievements from '@/pages/student/StudentAchievements'
import StudentPackages from '@/pages/student/StudentPackages'
import StudentProgress from '@/pages/student/StudentProgress'
import StudentReferrals from '@/pages/student/StudentReferrals'

// Instructor extra pages
import InstructorPro from '@/pages/instructor/InstructorPro'
import InstructorReport from '@/pages/instructor/InstructorReport'
import InstructorPayouts from '@/pages/instructor/InstructorPayouts'
import InstructorEarnings from '@/pages/instructor/InstructorEarnings'
import InstructorIssueCertificate from '@/pages/instructor/InstructorIssueCertificate'

// Student extra pages (M3)
import StudentCertificates from '@/pages/student/StudentCertificates'

// Batch Q — novas páginas
import StudentHistory from '@/pages/student/StudentHistory'

// Batch R — novas páginas
import StudentSupport from '@/pages/student/StudentSupport'
import InstructorVehicles from '@/pages/instructor/InstructorVehicles'
import InstructorSupport from '@/pages/instructor/InstructorSupport'
import AdminSupport from '@/pages/admin/AdminSupport'

// Batch S — novas páginas
import InstructorGallery from '@/pages/instructor/InstructorGallery'

// Batch T — novas páginas
import FaqPage from '@/pages/public/FaqPage'
import AdminFaq from '@/pages/admin/AdminFaq'
import StudentNotificationSettings from '@/pages/student/StudentNotificationSettings'
import InstructorNotificationSettings from '@/pages/instructor/InstructorNotificationSettings'

// Batch U — novas páginas
import InstructorChatSettings from '@/pages/instructor/InstructorChatSettings'

// Batch V — novas páginas
import GoogleCalendarCallbackPage from '@/pages/public/GoogleCalendarCallbackPage'
import AdminNotifications from '@/pages/admin/AdminNotifications'

// Admin extra pages (M2)
import AdminInstructorApproval from '@/pages/admin/AdminInstructorApproval'

// Admin extra pages (Batch N)
import AdminExports from '@/pages/admin/AdminExports'

// Batch O — novas páginas
import InstructorPackages from '@/pages/instructor/InstructorPackages'
import AdminPayouts from '@/pages/admin/AdminPayouts'

// Batch P — novas páginas
import AdminPromoCodes from '@/pages/admin/AdminPromoCodes'
import AdminAnnouncements from '@/pages/admin/AdminAnnouncements'
import AdminAuditLog from '@/pages/admin/AdminAuditLog'

// Admin pages
import AdminDashboard from '@/pages/admin/AdminDashboard'
import AdminInstructors from '@/pages/admin/AdminInstructors'
import AdminDisputes from '@/pages/admin/AdminDisputes'
import AdminFinancial from '@/pages/admin/AdminFinancial'
import AdminCoupons from '@/pages/admin/AdminCoupons'
import AdminUsers from '@/pages/admin/AdminUsers'
import AdminUserDetail from '@/pages/admin/AdminUserDetail'
import AdminPlatformSettings from '@/pages/admin/AdminPlatformSettings'
import AdminAnalytics from '@/pages/admin/AdminAnalytics'

// Route guards
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { RoleRoute } from '@/routes/RoleRoute'

// Layouts
import PublicLayout from '@/components/layout/PublicLayout'
import StudentLayout from '@/components/layout/StudentLayout'
import InstructorLayout from '@/components/layout/InstructorLayout'
import AdminLayout from '@/components/layout/AdminLayout'

function App() {
  const { initialized } = useAuth()

  // Null-render for the brief moment before INITIAL_SESSION fires from localStorage
  // (~1-2 frames, imperceptible). After that, public routes render immediately even
  // while the profile DB fetch is still in flight.
  if (!initialized) return <CarLoadingScreen />

  return (
    <AppErrorBoundary>
    <OfflineBanner />
    <PwaInstallBanner />
    <CookieConsent />
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/instrutores" element={<InstructorsPage />} />
        <Route path="/instrutores/:slug" element={<InstructorProfilePage />} />
        <Route path="/como-funciona" element={<HowItWorksPage />} />
        <Route path="/seja-instrutor" element={<BeInstructorPage />} />
        <Route path="/ajuda" element={<HelpPage />} />
        <Route path="/politica-cancelamento" element={<CancellationPolicyPage />} />
        <Route path="/seguranca" element={<SecurityPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route path="/presentes" element={<GiftCardPage />} />
        <Route path="/faq" element={<FaqPage />} />
      </Route>

      {/* Auth routes (no layout) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/google-calendar/callback" element={<GoogleCalendarCallbackPage />} />

      {/* Student routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="student">
              <StudentLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/aluno/dashboard" element={<StudentDashboard />} />
        <Route path="/aluno/agendar/:instructorId" element={<BookingFlow />} />
        <Route path="/aluno/aulas/:bookingId" element={<StudentBookingDetail />} />
        <Route path="/aluno/perfil" element={<StudentProfile />} />
        <Route path="/aluno/avaliacoes" element={<StudentReviews />} />
        <Route path="/aluno/mensagens" element={<MessagesPage />} />
        <Route path="/aluno/pagamentos" element={<StudentPayments />} />
        <Route path="/aluno/notificacoes" element={<StudentNotificationSettings />} />
        <Route path="/aluno/notificacoes/inbox" element={<NotificationsPage />} />
        <Route path="/aluno/premium" element={<StudentPremium />} />
        <Route path="/aluno/simulado" element={<StudentQuiz />} />
        <Route path="/aluno/conquistas" element={<StudentAchievements />} />
        <Route path="/aluno/pacotes" element={<StudentPackages />} />
        <Route path="/aluno/progresso" element={<StudentProgress />} />
        <Route path="/aluno/indicacoes" element={<StudentReferrals />} />
        <Route path="/aluno/certificados" element={<StudentCertificates />} />
        <Route path="/aluno/historico" element={<StudentHistory />} />
        <Route path="/aluno/suporte"   element={<StudentSupport />} />
      </Route>

      {/* Instructor routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="instructor">
              <InstructorLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/instrutor/dashboard" element={<InstructorDashboard />} />
        <Route path="/instrutor/agenda" element={<InstructorAgenda />} />
        <Route path="/instrutor/aulas" element={<InstructorLessons />} />
        <Route path="/instrutor/financeiro" element={<InstructorFinancial />} />
        <Route path="/instrutor/perfil" element={<InstructorEditProfile />} />
        <Route path="/instrutor/alunos" element={<InstructorStudents />} />
        <Route path="/instrutor/mensagens" element={<MessagesPage />} />
        <Route path="/instrutor/notificacoes" element={<InstructorNotificationSettings />} />
        <Route path="/instrutor/notificacoes/inbox" element={<NotificationsPage />} />
        <Route path="/instrutor/pro" element={<InstructorPro />} />
        <Route path="/instrutor/relatorio" element={<InstructorReport />} />
        <Route path="/instrutor/saques" element={<InstructorPayouts />} />
        <Route path="/instrutor/ganhos" element={<InstructorEarnings />} />
        <Route path="/instrutor/certificados" element={<InstructorIssueCertificate />} />
        <Route path="/instrutor/pacotes"  element={<InstructorPackages />} />
        <Route path="/instrutor/veiculos" element={<InstructorVehicles />} />
        <Route path="/instrutor/suporte"  element={<InstructorSupport />} />
        <Route path="/instrutor/galeria"  element={<InstructorGallery />} />
        <Route path="/instrutor/chat-templates" element={<InstructorChatSettings />} />
      </Route>

      {/* Student onboarding (no full layout) */}
      <Route
        path="/aluno/onboarding"
        element={
          <ProtectedRoute>
            <RoleRoute role="student">
              <StudentOnboarding />
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Instructor onboarding (no full layout) */}
      <Route
        path="/instrutor/onboarding"
        element={
          <ProtectedRoute>
            <RoleRoute role="instructor">
              <InstructorOnboarding />
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="admin">
              <AdminLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/instrutores" element={<AdminInstructors />} />
        <Route path="/admin/disputas" element={<AdminDisputes />} />
        <Route path="/admin/financeiro" element={<AdminFinancial />} />
        <Route path="/admin/cupons" element={<AdminCoupons />} />
        <Route path="/admin/usuarios" element={<AdminUsers />} />
        <Route path="/admin/usuarios/:userId" element={<AdminUserDetail />} />
        <Route path="/admin/configuracoes" element={<AdminPlatformSettings />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/instrutores/aprovacao" element={<AdminInstructorApproval />} />
        <Route path="/admin/exportar" element={<AdminExports />} />
        <Route path="/admin/pagamentos" element={<AdminPayouts />} />
        <Route path="/admin/cupons-promo" element={<AdminPromoCodes />} />
        <Route path="/admin/anuncios" element={<AdminAnnouncements />} />
        <Route path="/admin/auditoria" element={<AdminAuditLog />} />
        <Route path="/admin/suporte"        element={<AdminSupport />} />
        <Route path="/admin/faq"            element={<AdminFaq />} />
        <Route path="/admin/notificacoes"   element={<AdminNotifications />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </AppErrorBoundary>
  )
}

export default App
